/**
 * @ipc/admin-ui · shared/api/query-keys — фабрика ключей TanStack Query по доменам.
 * Каждый домен экрана (фаза 2) использует свой раздел; invalidateQueries после мутаций
 * бьёт по префиксу домена. Стабильные ключи → предсказуемый кэш и точечная инвалидация.
 */

/** Параметры фильтра/пагинации, входящие в ключ запроса (сериализуемые примитивы). */
export type QueryParams = Readonly<Record<string, string | number | boolean | null | undefined>>;

export const queryKeys = {
  session: () => ['session'] as const,

  payments: {
    all: () => ['payments'] as const,
    list: (params: QueryParams) => ['payments', 'list', params] as const,
    detail: (uuid: string) => ['payments', 'detail', uuid] as const,
  },

  links: {
    all: () => ['links'] as const,
    list: (params: QueryParams) => ['links', 'list', params] as const,
    detail: (uuid: string) => ['links', 'detail', uuid] as const,
    watch: (uuid: string) => ['links', 'watch', uuid] as const,
  },

  analytics: {
    all: () => ['analytics'] as const,
    summary: (params: QueryParams) => ['analytics', 'summary', params] as const,
  },

  logs: {
    all: () => ['logs'] as const,
    list: (params: QueryParams) => ['logs', 'list', params] as const,
  },

  audit: {
    all: () => ['audit'] as const,
    list: (params: QueryParams) => ['audit', 'list', params] as const,
  },

  webhooks: {
    all: () => ['webhooks'] as const,
    subscriptions: () => ['webhooks', 'subscriptions'] as const,
    deliveries: (params: QueryParams) => ['webhooks', 'deliveries', params] as const,
  },

  actions: {
    all: () => ['actions'] as const,
    list: (params: QueryParams) => ['actions', 'list', params] as const,
    catalog: (merchantId: number | null) => ['actions', 'catalog', merchantId] as const,
  },

  merchants: {
    all: () => ['merchants'] as const,
    list: (params: QueryParams) => ['merchants', 'list', params] as const,
  },

  roles: {
    all: () => ['roles'] as const,
    list: () => ['roles', 'list'] as const,
  },

  users: {
    all: () => ['users'] as const,
    list: (params: QueryParams) => ['users', 'list', params] as const,
  },

  tenants: {
    all: () => ['tenants'] as const,
    list: () => ['tenants', 'list'] as const,
    detail: (slug: string) => ['tenants', 'detail', slug] as const,
    analyticsSummary: (slug: string, params: QueryParams) =>
      ['tenants', 'analytics', 'summary', slug, params] as const,
    analyticsTimeseries: (slug: string, params: QueryParams) =>
      ['tenants', 'analytics', 'timeseries', slug, params] as const,
    analyticsBreakdown: (slug: string, groupBy: string, params: QueryParams) =>
      ['tenants', 'analytics', 'breakdown', slug, groupBy, params] as const,
    admins: (slug: string, params: QueryParams) => ['tenants', 'admins', slug, params] as const,
    roles: (slug: string) => ['tenants', 'roles', slug] as const,
  },

  /** ИКПУ / МХИК — реестр tasnif.soliq.uz (proxy backend). */
  ikpu: {
    all: () => ['ikpu'] as const,
    search: (q: string) => ['ikpu', 'search', q] as const,
    detail: (code: string) => ['ikpu', 'detail', code] as const,
  },
} as const;
